import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "app/store";
import { QUICK_FILTER_DEFAULT_TYPE } from "constants/billing";
import { dispatchTabSyncMessage } from "hooks/use-sync-between-tabs.hook";
import { getSelectedIds } from "utils/common";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

interface RecentInvoiceInfo {
  saleId: number;
  patientId: number;
}

interface BillingFiltersState {
  quickFilterCode: string;
  selectedPrintSaleIds: Record<number, boolean>;
  openBulkPrintModal: boolean;
  openBulkEmailModal: boolean;
  openBulkDarwillModal: boolean;
  openBulkCollectionsModal: boolean;
  showCheckboxes: boolean;
  showRecentInvoices: boolean;
  refreshRecentInvoices: boolean;
  recentInvoices: Record<string, RecentInvoiceInfo[]>;
  invoiceDialogSaleId?: number;
  purchaseAgreementDialogSaleId?: number;
}

const showRecentInvoicesStorage = setupLocalStorageAPI("showRecentInvoices");

const recentInvoicesStorage = setupLocalStorageAPI("recentInvoices");

const initialState: BillingFiltersState = {
  quickFilterCode: QUICK_FILTER_DEFAULT_TYPE,
  selectedPrintSaleIds: {},
  openBulkPrintModal: false,
  openBulkEmailModal: false,
  openBulkDarwillModal: false,
  openBulkCollectionsModal: false,
  showCheckboxes: false,
  showRecentInvoices: showRecentInvoicesStorage.getData(true),
  recentInvoices: recentInvoicesStorage.getData({}),
  invoiceDialogSaleId: undefined,
  refreshRecentInvoices: false,
  purchaseAgreementDialogSaleId: undefined
};

const MAX_RECENT_INVOICES = 1;

const billingFiltersSlice = createSlice({
  name: "billing-filters",
  initialState,
  reducers: {
    applyQuickFilter(state, action: PayloadAction<string>) {
      const { payload } = action;
      state.quickFilterCode = payload;
    },
    addSelectedPrintSaleId: (
      state,
      action: PayloadAction<{ id: number; selected: boolean }>
    ) => {
      const { id, selected } = action.payload;
      state.selectedPrintSaleIds[id] = selected;
    },
    bulkSelectedPrintSaleId: (
      state,
      action: PayloadAction<{ ids: number[] }>
    ) => {
      const { ids } = action.payload;
      state.selectedPrintSaleIds = {};
      ids.forEach((id) => {
        state.selectedPrintSaleIds[id] = true;
      });
    },
    toggleOpenBulkPrintModal(state) {
      state.openBulkPrintModal = !state.openBulkPrintModal;
    },
    toggleOpenBulkEmailModal(state) {
      state.openBulkEmailModal = !state.openBulkEmailModal;
    },
    toggleOpenBulkDarwillModal(state) {
      state.openBulkDarwillModal = !state.openBulkDarwillModal;
    },
    toggleOpenBulkCollectionsModal(state) {
      state.openBulkCollectionsModal = !state.openBulkCollectionsModal;
    },
    toggleShowCheckboxes: (state) => {
      const newState = !state.showCheckboxes;
      state.showCheckboxes = newState;

      if (!newState) {
        state.selectedPrintSaleIds = {};
      }
    },

    setInvoiceDialogSaleId(state, action: PayloadAction<number | undefined>) {
      const { payload } = action;
      state.invoiceDialogSaleId = payload;
    },

    setPurchaseAgreementDialogSaleId(
      state,
      action: PayloadAction<number | undefined>
    ) {
      const { payload } = action;
      state.purchaseAgreementDialogSaleId = payload;
    },

    clearRecentInvoices(
      state,
      action: PayloadAction<{
        practiceId: number;
        doNotSendSyncMessage?: boolean;
      }>
    ) {
      const { practiceId, doNotSendSyncMessage } = action.payload;

      const recentInvoices = !state.recentInvoices
        ? {}
        : { ...state.recentInvoices };

      recentInvoices[practiceId] = [];
      state.recentInvoices = recentInvoices;
      state.showRecentInvoices = true;
      state.refreshRecentInvoices = false;
      showRecentInvoicesStorage.updateData(true);
      recentInvoicesStorage.updateData(recentInvoices);

      if (!doNotSendSyncMessage) {
        dispatchTabSyncMessage({
          action: "clearRecentInvoices",
          payload: {
            practiceId
          }
        });
      }
    },

    addRecentInvoice(
      state,
      action: PayloadAction<{
        saleId: number;
        patientId: number;
        practiceId: number | undefined;
        doNotSendSyncMessage?: boolean;
      }>
    ) {
      const { saleId, practiceId, patientId, doNotSendSyncMessage } =
        action.payload;

      if (!practiceId || !saleId || !patientId) return;
      if (!state.recentInvoices) {
        state.recentInvoices = {};
      }

      const practiceRecentInvoices = state.recentInvoices[practiceId] || [];

      const result: RecentInvoiceInfo[] = [{ saleId, patientId }];

      const keySet = new Set<string>();
      keySet.add(`${patientId}-${saleId}`);

      practiceRecentInvoices.forEach((invoice) => {
        const key = `${invoice.patientId}-${invoice.saleId}`;
        if (!keySet.has(key) && result.length < MAX_RECENT_INVOICES) {
          keySet.add(key);
          result.push(invoice);
        }
      });

      state.showRecentInvoices = true;
      state.refreshRecentInvoices = true;
      state.recentInvoices[practiceId] = result;
      recentInvoicesStorage.updateData(state.recentInvoices);

      if (!doNotSendSyncMessage) {
        dispatchTabSyncMessage({
          action: "addRecentInvoice",
          payload: action.payload
        });
      }
    },
    setRefreshRecentInvoices(
      state,
      action: PayloadAction<{
        refreshRecentInvoices: boolean;
        doNotSendSyncMessage?: boolean;
      }>
    ) {
      const { refreshRecentInvoices, doNotSendSyncMessage } = action.payload;
      state.refreshRecentInvoices = refreshRecentInvoices;
      if (!doNotSendSyncMessage) {
        dispatchTabSyncMessage({
          action: "setRefreshRecentInvoices",
          payload: action.payload
        });
      }
    },
    toggleShowRecentInvoices(
      state,
      action: PayloadAction<{
        value?: boolean;
        doNotSendSyncMessage?: boolean;
      }>
    ) {
      const { value, doNotSendSyncMessage } = action.payload;
      if (value === undefined) {
        state.showRecentInvoices = !state.showRecentInvoices;
      } else {
        state.showRecentInvoices = value;
      }
      if (state.showRecentInvoices) {
        state.refreshRecentInvoices = true;
      }

      showRecentInvoicesStorage.updateData(state.showRecentInvoices);
      if (!doNotSendSyncMessage) {
        dispatchTabSyncMessage({
          action: "toggleShowRecentInvoices",
          payload: action.payload
        });
      }
    }
  }
});

export const selectQuickFilterCodeValue = (state: RootState) =>
  state.billingFilters.quickFilterCode;

export const selectSelectedPrintSaleIds = (state: RootState) =>
  state.billingFilters.selectedPrintSaleIds;

export const selectSalesSelectionState = (
  state: RootState,
  salesCount: number
) => {
  const selectedIds = getSelectedIds(state.billingFilters.selectedPrintSaleIds);
  if (selectedIds.length === 0) return "none";
  if (selectedIds.length === salesCount) return "all";
  return "intermediate";
};

export const selectSelectedCount = (state: RootState) =>
  getSelectedIds(state.billingFilters.selectedPrintSaleIds).length;

export const selectOpenBulkPrintModal = (state: RootState) =>
  state.billingFilters.openBulkPrintModal;

export const selectOpenBulkEmailModal = (state: RootState) =>
  state.billingFilters.openBulkEmailModal;

export const selectOpenBulkDarwillModal = (state: RootState) =>
  state.billingFilters.openBulkDarwillModal;

export const selectOpenBulkCollectionsModal = (state: RootState) =>
  state.billingFilters.openBulkCollectionsModal;

export const selectShowCheckboxes = (state: RootState) =>
  state.billingFilters.showCheckboxes;

export const selectShowRecentInvoices = (state: RootState) =>
  state.billingFilters.showRecentInvoices;

export const selectRefreshRecentInvoices = (state: RootState) =>
  state.billingFilters.refreshRecentInvoices;

export const selectRecentInvoices = (state: RootState) =>
  state.billingFilters.recentInvoices;

export const selectInvoiceDialogSaleId = (state: RootState) =>
  state.billingFilters.invoiceDialogSaleId;
export const selectPurchaseAgreementDialogSaleId = (state: RootState) =>
  state.billingFilters.purchaseAgreementDialogSaleId;

export const {
  applyQuickFilter,
  bulkSelectedPrintSaleId,
  addSelectedPrintSaleId,
  toggleOpenBulkPrintModal,
  toggleOpenBulkEmailModal,
  toggleOpenBulkDarwillModal,
  toggleOpenBulkCollectionsModal,
  toggleShowCheckboxes,
  addRecentInvoice,
  toggleShowRecentInvoices,
  setInvoiceDialogSaleId,
  setPurchaseAgreementDialogSaleId,
  clearRecentInvoices,
  setRefreshRecentInvoices
} = billingFiltersSlice.actions;

export default billingFiltersSlice.reducer;
